<script lang="ts">
  import { MarkupHelper } from '../helpers/markup-helper';
  import { ChatKitWidgetHelper } from '../helpers/chatkit-widget-helper';
  import type {
    IChatKitAction,
    IChatKitWidgetInstance,
    IChatKitWidgetNode,
  } from '../interfaces/chatkit-widget.interface';
  import { chatKitIconMarkup } from '../helpers/chatkit-icons';

  export let node: IChatKitWidgetNode;
  export let widget: IChatKitWidgetInstance;
  export let formPayload: Record<string, any> = {};

  const md = new MarkupHelper();
  let failedImageSrc: string | undefined;

  $: children = normalizeChildren(node?.children);
  $: type = node?.type;
  $: hasImageSource =
    typeof node?.src === 'string' && node.src.trim().length > 0;
  $: imageUnavailable =
    type === 'Image' &&
    (!hasImageSource || (failedImageSrc != null && failedImageSrc === node.src));

  const COLOR_TOKENS: Record<string, string> = {
    primary: '#111827',
    secondary: '#5f6673',
    muted: '#64748b',
    danger: '#b42318',
    destructive: '#b42318',
    success: '#027a48',
    warning: '#b54708',
  };

  function normalizeChildren(
    value: IChatKitWidgetNode[] | IChatKitWidgetNode | undefined
  ): IChatKitWidgetNode[] {
    if (!value) return [];
    return Array.isArray(value) ? value : [value];
  }

  function styleFor(n: IChatKitWidgetNode): string {
    const styles: Record<string, string> = {};
    const spacing = (key: 'padding' | 'margin') => {
      const value = n[key];
      if (value == null) return;
      if (typeof value === 'number') styles[key] = spacingUnit(value);
      else if (typeof value === 'string') styles[key] = value;
      else if (typeof value === 'object') {
        const top = value.top ?? value.y;
        const right = value.right ?? value.x;
        const bottom = value.bottom ?? value.y;
        const left = value.left ?? value.x;
        if (top != null) styles[`${key}-top`] = spacingUnit(top);
        if (right != null) styles[`${key}-right`] = spacingUnit(right);
        if (bottom != null) styles[`${key}-bottom`] = spacingUnit(bottom);
        if (left != null) styles[`${key}-left`] = spacingUnit(left);
      }
    };
    spacing('padding');
    spacing('margin');

    const dimensionalSize = dimensionValue(n.size);
    mapStyle(styles, 'width', n.width ?? dimensionalSize);
    mapStyle(styles, 'height', n.height ?? dimensionalSize);
    mapStyle(styles, 'min-width', n.minWidth ?? n.minSize);
    mapStyle(styles, 'min-height', n.minHeight ?? n.minSize);
    mapStyle(styles, 'max-width', n.maxWidth ?? n.maxSize);
    mapStyle(styles, 'max-height', n.maxHeight ?? n.maxSize);
    mapStyle(styles, 'flex', n.flex);
    mapStyle(styles, 'gap', n.gap);
    mapStyle(styles, 'aspect-ratio', n.aspectRatio);
    mapStyle(styles, 'background', colorValue(n.background));
    mapStyle(styles, 'color', colorValue(n.color));
    mapStyle(styles, 'text-align', n.textAlign);

    if (n.maxLines) {
      styles.display = '-webkit-box';
      styles.overflow = 'hidden';
      styles['-webkit-line-clamp'] = String(n.maxLines);
      styles['-webkit-box-orient'] = 'vertical';
    }
    if (n.direction) styles['flex-direction'] = n.direction;
    if (n.align) styles['align-items'] = alignValue(n.align);
    if (n.justify) styles['justify-content'] = justifyValue(n.justify);
    if (n.wrap) styles['flex-wrap'] = n.wrap;
    if (n.radius) styles['border-radius'] = radiusValue(n.radius);
    if (n.border != null) styles.border = borderValue(n.border);

    return Object.entries(styles)
      .filter(([, value]) => value != null && value !== '')
      .map(([key, value]) => `${key}: ${value}`)
      .join('; ');
  }

  function unit(value: string | number): string {
    return typeof value === 'number' ? `${value}px` : value;
  }

  function dimensionValue(value: any): string | number | undefined {
    if (typeof value === 'number') return value;
    if (typeof value !== 'string') return undefined;
    if (
      value === 'auto' ||
      value === 'fit-content' ||
      value === 'max-content' ||
      value === 'min-content' ||
      value.startsWith('calc(') ||
      value.startsWith('min(') ||
      value.startsWith('max(') ||
      value.startsWith('clamp(') ||
      /^-?\d*\.?\d+(px|rem|em|%|vh|vw|vmin|vmax|ch|ex|lh|rlh)$/.test(value)
    ) {
      return value;
    }
    return undefined;
  }

  function spacingUnit(value: string | number): string {
    return typeof value === 'number' ? `${value * 4}px` : value;
  }

  function mapStyle(
    styles: Record<string, string>,
    key: string,
    value: string | number | undefined
  ) {
    if (value == null) return;
    if (key === 'flex' || key === 'aspect-ratio') {
      styles[key] = String(value);
      return;
    }
    if (key === 'gap') {
      styles[key] = spacingUnit(value);
      return;
    }
    styles[key] = unit(value);
  }

  function colorValue(value: any): string | undefined {
    if (!value) return undefined;
    if (typeof value === 'string') return COLOR_TOKENS[value] ?? value;
    return value.light ?? value.dark;
  }

  function alignValue(value: string): string {
    return value === 'start' || value === 'end' ? `flex-${value}` : value;
  }

  function justifyValue(value: string): string {
    const map: Record<string, string> = {
      start: 'flex-start',
      end: 'flex-end',
      between: 'space-between',
      around: 'space-around',
      evenly: 'space-evenly',
    };
    return map[value] ?? value;
  }

  function radiusValue(value: string): string {
    const map: Record<string, string> = {
      none: '0',
      '2xs': '2px',
      xs: '4px',
      sm: '6px',
      md: '8px',
      lg: '10px',
      xl: '12px',
      '2xl': '16px',
      '3xl': '20px',
      '4xl': '24px',
      full: '999px',
    };
    return map[value] ?? value;
  }

  function borderValue(value: any): string {
    if (typeof value === 'number') return `${value}px solid currentColor`;
    if (typeof value === 'object') {
      const size = value.size ?? 1;
      const style = value.style ?? 'solid';
      const color = colorValue(value.color) ?? 'currentColor';
      return `${unit(size)} ${style} ${color}`;
    }
    return String(value);
  }

  function action(action: IChatKitAction | undefined): void {
    if (!action) return;
    ChatKitWidgetHelper.handleAction(action, {
      functionName: widget.functionName,
      widgetId: widget.id,
      questionId: widget.questionId,
      data: widget.data,
    });
  }

  function clickableKeydown(event: KeyboardEvent): void {
    if (!node.onClickAction) return;
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    action(node.onClickAction);
  }

  function horizontalListKeydown(event: KeyboardEvent): void {
    if (node.orientation !== 'horizontal') return;
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    event.preventDefault();
    const target = event.currentTarget as HTMLElement;
    const distance = Math.max(target.clientWidth * 0.75, 120);
    target.scrollBy({
      left: event.key === 'ArrowLeft' ? -distance : distance,
      behavior: 'smooth',
    });
  }

  function submitAction(
    actionConfig: IChatKitAction | undefined,
    event: SubmitEvent
  ): void {
    event.preventDefault();
    if (!actionConfig) return;
    const data = Object.fromEntries(new FormData(event.currentTarget as HTMLFormElement));
    ChatKitWidgetHelper.handleAction(
      actionConfig,
      {
        functionName: widget.functionName,
        widgetId: widget.id,
        questionId: widget.questionId,
        data: widget.data,
      },
      data
    );
  }

  function inputType(n: IChatKitWidgetNode): string {
    if (n.type === 'DatePicker') return 'date';
    if (n.type === 'Checkbox') return 'checkbox';
    if (n.type === 'RadioGroup') return 'radio';
    return n.type === 'Textarea' ? 'textarea' : 'text';
  }

  function textClass(n: IChatKitWidgetNode): string {
    return [
      'cs-chatkit-text',
      n.size ? `cs-chatkit-text-${n.size}` : '',
      n.weight ? `cs-chatkit-weight-${n.weight}` : '',
      n.truncate ? 'cs-chatkit-truncate' : '',
    ]
      .filter(Boolean)
      .join(' ');
  }

  function imageStyleFor(n: IChatKitWidgetNode): string {
    const styles: string[] = [styleFor(n)];
    const size =
      dimensionValue(n.size) ??
      (n.width && n.height ? dimensionValue(n.width) : undefined);
    if (size != null) {
      const fixedSize = unit(size);
      styles.push(`flex: 0 0 ${fixedSize}`);
      styles.push(`min-width: ${fixedSize}`);
      styles.push(`max-width: ${fixedSize}`);
      if (n.height == null) styles.push(`height: ${fixedSize}`);
    } else if (n.width != null) {
      styles.push(`flex: 0 0 ${unit(n.width)}`);
    } else {
      styles.push('flex: 0 0 auto');
      if (n.flush || n.aspectRatio != null) {
        styles.push('width: 100%');
      }
    }
    styles.push(`object-fit: ${n.fit ?? 'cover'}`);
    styles.push(`object-position: ${n.position ?? 'center'}`);
    return styles.filter(Boolean).join('; ');
  }

  function iconMarkup(name: string | undefined): string | undefined {
    return chatKitIconMarkup(name);
  }

  function imageUnavailableLabel(n: IChatKitWidgetNode): string {
    return n.alt ? `${n.alt} image unavailable` : 'Image unavailable';
  }
</script>

{#if type === 'Card' || type === 'Basic'}
  <!-- svelte-ignore a11y_no_static_element_interactions -->
  <!-- svelte-ignore a11y_no_noninteractive_tabindex -->
  <div
    class={`cs-chatkit-card cs-chatkit-size-${node.size ?? 'md'}`}
    class:cs-chatkit-card-collapsed={node.collapsed}
    class:cs-chatkit-clickable={!!node.onClickAction}
    role={node.onClickAction ? 'button' : undefined}
    tabindex={node.onClickAction ? 0 : undefined}
    style={styleFor(node)}
    on:click={() => action(node.onClickAction)}
    on:keydown={clickableKeydown}
  >
    {#if !node.collapsed}
      {#each children as child (child.key ?? child)}
        <svelte:self node={child} {widget} {formPayload} />
      {/each}
    {/if}
    {#if node.status?.text}
      <div class="cs-chatkit-status">{node.status.text}</div>
    {/if}
    {#if node.confirm || node.cancel}
      <div class="cs-chatkit-actions">
        {#if node.cancel}
          <button
            type="button"
            class="cs-chatkit-button cs-chatkit-button-secondary"
            on:click|stopPropagation={() => action(node.cancel.action)}
          >
            {node.cancel.label}
          </button>
        {/if}
        {#if node.confirm}
          <button
            type="button"
            class="cs-chatkit-button cs-chatkit-button-primary"
            on:click|stopPropagation={() => action(node.confirm.action)}
          >
            {node.confirm.label}
          </button>
        {/if}
      </div>
    {/if}
  </div>
{:else if type === 'ListView'}
  <!-- svelte-ignore a11y_no_noninteractive_tabindex (scrollable region supports arrow-key navigation) -->
  <div
    class="cs-chatkit-list"
    class:cs-chatkit-list-horizontal={node.orientation === 'horizontal'}
    role={node.orientation === 'horizontal' ? 'region' : undefined}
    aria-label={node.orientation === 'horizontal'
      ? (node.label ?? 'Scrollable items')
      : undefined}
    tabindex={node.orientation === 'horizontal' ? 0 : undefined}
    style={styleFor(node)}
    on:keydown={horizontalListKeydown}
  >
    {#each children.slice(0, node.limit === 'auto' || !node.limit ? children.length : node.limit) as child (child.key ?? child)}
      <svelte:self node={child} {widget} {formPayload} />
    {/each}
  </div>
{:else if type === 'ListViewItem'}
  {#if node.onClickAction}
    <div
      class="cs-chatkit-list-item"
      class:cs-chatkit-clickable={!!node.onClickAction}
      role="button"
      tabindex="0"
      style={styleFor(node)}
      on:click={() => action(node.onClickAction)}
      on:keydown={clickableKeydown}
    >
      {#each children as child (child.key ?? child)}
        <svelte:self node={child} {widget} {formPayload} />
      {/each}
    </div>
  {:else}
    <div class="cs-chatkit-list-item" style={styleFor(node)}>
      {#each children as child (child.key ?? child)}
        <svelte:self node={child} {widget} {formPayload} />
      {/each}
    </div>
  {/if}
{:else if ['Box', 'Row', 'Col', 'Transition'].includes(type)}
  <div
    class={`cs-chatkit-box cs-chatkit-${type.toLowerCase()}`}
    style={styleFor({ ...node, direction: type === 'Row' ? 'row' : type === 'Col' ? 'column' : node.direction })}
  >
    {#each children as child (child.key ?? child)}
      <svelte:self node={child} {widget} {formPayload} />
    {/each}
  </div>
{:else if type === 'Form'}
  <form
    class="cs-chatkit-form"
    style={styleFor(node)}
    on:submit={(event) => submitAction(node.onSubmitAction, event)}
  >
    {#each children as child (child.key ?? child)}
      <svelte:self node={child} {widget} {formPayload} />
    {/each}
  </form>
{:else if type === 'Button'}
  <button
    type={node.submit || node.type === 'submit' ? 'submit' : 'button'}
    class={`cs-chatkit-button cs-chatkit-button-${node.style ?? node.variant ?? 'secondary'} cs-chatkit-button-${node.size ?? 'md'}`}
    class:cs-chatkit-pill={node.pill}
    class:cs-chatkit-block={node.block}
    disabled={node.disabled}
    style={styleFor(node)}
    on:click|stopPropagation={() =>
      !node.submit && node.type !== 'submit' && action(node.onClickAction)}
  >
    {#if node.iconStart}
      <span class="cs-chatkit-icon cs-chatkit-button-icon" aria-hidden="true">
        {#if iconMarkup(node.iconStart)}
          <svg viewBox="0 0 24 24" focusable="false">
            {@html iconMarkup(node.iconStart)}
          </svg>
        {:else}
          <span class="cs-chatkit-icon-fallback">{node.iconStart}</span>
        {/if}
      </span>
    {/if}
    {node.label}
    {#if node.iconEnd}
      <span class="cs-chatkit-icon cs-chatkit-button-icon" aria-hidden="true">
        {#if iconMarkup(node.iconEnd)}
          <svg viewBox="0 0 24 24" focusable="false">
            {@html iconMarkup(node.iconEnd)}
          </svg>
        {:else}
          <span class="cs-chatkit-icon-fallback">{node.iconEnd}</span>
        {/if}
      </span>
    {/if}
  </button>
{:else if type === 'Badge'}
  <span
    class={`cs-chatkit-badge cs-chatkit-badge-${node.color ?? 'secondary'} cs-chatkit-badge-${node.variant ?? 'soft'} cs-chatkit-badge-${node.size ?? 'md'}`}
    class:cs-chatkit-pill={node.pill}
  >
    {node.label}
  </span>
{:else if type === 'Title'}
  <div class={`cs-chatkit-title cs-chatkit-title-${node.size ?? 'md'} cs-chatkit-weight-${node.weight ?? 'semibold'}`} style={styleFor(node)}>
    {node.value}
  </div>
{:else if type === 'Text' || type === 'Caption' || type === 'Label'}
  {#if node.editable}
    <input
      class="cs-chatkit-input"
      name={typeof node.editable === 'object' ? node.editable.name : undefined}
      value={node.value}
      placeholder={typeof node.editable === 'object' ? node.editable.placeholder : undefined}
      required={typeof node.editable === 'object' ? node.editable.required : false}
      pattern={typeof node.editable === 'object' ? node.editable.pattern : undefined}
      style={styleFor(node)}
    />
  {:else}
    <div class={textClass(node)} style={styleFor(node)}>
      {node.value}
    </div>
  {/if}
{:else if type === 'Markdown'}
  <div class="cs-chatkit-markdown" style={styleFor(node)}>
    {@html md.render(node.value ?? '', undefined)}
  </div>
{:else if type === 'Image'}
  {#if imageUnavailable}
    <div
      class="cs-chatkit-image cs-chatkit-image-placeholder"
      class:cs-chatkit-image-frame={node.frame}
      class:cs-chatkit-image-flush={node.flush}
      role="img"
      aria-label={imageUnavailableLabel(node)}
      style={imageStyleFor(node)}
    >
      <span class="cs-chatkit-icon" aria-hidden="true">
        <svg viewBox="0 0 24 24" focusable="false">
          {@html iconMarkup('image-off')}
        </svg>
      </span>
    </div>
  {:else}
    <img
      class="cs-chatkit-image"
      class:cs-chatkit-image-frame={node.frame}
      class:cs-chatkit-image-flush={node.flush}
      src={node.src}
      alt={node.alt ?? ''}
      style={imageStyleFor(node)}
      on:error={() => (failedImageSrc = node.src)}
    />
  {/if}
{:else if type === 'Icon'}
  <span class="cs-chatkit-icon" style={styleFor(node)} aria-hidden="true">
    {#if iconMarkup(node.name)}
      <svg viewBox="0 0 24 24" focusable="false">
        {@html iconMarkup(node.name)}
      </svg>
    {:else}
      <span class="cs-chatkit-icon-fallback">{node.name}</span>
    {/if}
  </span>
{:else if type === 'Divider'}
  <div
    class="cs-chatkit-divider"
    style={`${styleFor(node)}; height: ${unit(node.size ?? 1)}; margin-block: ${unit(node.spacing ?? 8)}`}
  ></div>
{:else if type === 'Spacer'}
  <span
    class="cs-chatkit-spacer"
    style={`min-width: ${unit(node.minSize ?? 8)}; min-height: ${unit(node.minSize ?? 8)}`}
  ></span>
{:else if type === 'Select'}
  <select
    class="cs-chatkit-input"
    name={node.name}
    disabled={node.disabled}
    required={node.required}
    on:change={() => action(node.onChangeAction)}
  >
    {#if node.placeholder}<option value="">{node.placeholder}</option>{/if}
    {#each node.options ?? [] as option}
      <option value={option.value} selected={option.value === node.defaultValue}>{option.label}</option>
    {/each}
  </select>
{:else if ['DatePicker', 'Input', 'Textarea', 'Checkbox', 'RadioGroup'].includes(type)}
  {#if inputType(node) === 'textarea'}
    <textarea
      class="cs-chatkit-input"
      name={node.name}
      placeholder={node.placeholder}
      disabled={node.disabled}
      required={node.required}
      on:change={() => action(node.onChangeAction)}
    >{node.defaultValue ?? node.value ?? ''}</textarea>
  {:else}
    <input
      class="cs-chatkit-input"
      type={inputType(node)}
      name={node.name}
      value={node.defaultValue ?? node.value ?? ''}
      placeholder={node.placeholder}
      disabled={node.disabled}
      required={node.required}
      min={node.min}
      max={node.max}
      on:change={() => action(node.onChangeAction)}
    />
  {/if}
{:else if type === 'Table'}
  <table class="cs-chatkit-table">
    {#if node.columns}
      <thead>
        <tr>{#each node.columns as column}<th>{column.label ?? column.key}</th>{/each}</tr>
      </thead>
    {/if}
    <tbody>
      {#each node.rows ?? [] as row}
        <tr>
          {#each node.columns ?? Object.keys(row).map((key) => ({ key })) as column}
            <td>{row[column.key]}</td>
          {/each}
        </tr>
      {/each}
    </tbody>
  </table>
{:else if type === 'Chart'}
  <div class="cs-chatkit-chart" style={styleFor(node)}>
    {#if node.title}<div class="cs-chatkit-title cs-chatkit-title-sm">{node.title}</div>{/if}
    {#each node.data ?? [] as item}
      <div class="cs-chatkit-chart-row">
        <span>{item.label ?? item.name}</span>
        <span>{item.value}</span>
      </div>
    {/each}
  </div>
{:else}
  <div class="cs-chatkit-unknown" style={styleFor(node)}>
    {#each children as child (child.key ?? child)}
      <svelte:self node={child} {widget} {formPayload} />
    {/each}
  </div>
{/if}
